import { useState } from "react";
import { Link } from "@inertiajs/react";
import { Image as ImageIcon, User, Globe, Twitch, Save } from "lucide-react";

type PlayerFormProps = {
    onSubmit: (data: any) => void;
    player?: any;
    teamId: number;
};

export default function PlayerForm({ onSubmit, player, teamId }: PlayerFormProps) {
    const isNew = player === null || player === undefined;

    const [form, setForm] = useState({
        name: player?.name ?? "",
        nickname: player?.nickname ?? "",
        country: player?.country ?? "",
        twitter: player?.twitter ?? "",
        twitch: player?.twitch ?? "",
        hltv: player?.hltv ?? "",
        teamId: teamId,
        image: player?.image ?? "",
    });

    function updateField(key: string, value: string | number) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        onSubmit(form);
    }

    const inputClass =
        "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100";

    const labelClass = "mb-2 block text-sm font-medium text-gray-700";

    return (
        <form onSubmit={handleSubmit} className="mx-auto w-full max-w-4xl">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-6 py-5 sm:px-8">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-900">
                                {isNew ? "Create Player" : "Edit Player"}
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                Manage player profile information, social links, and image.
                            </p>
                        </div>

                        <Link
                            href="/admin/teams"
                            className="inline-flex items-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        >
                            Cancel
                        </Link>
                    </div>
                </div>

                <div className="grid gap-8 px-6 py-6 sm:px-8 lg:grid-cols-[1fr_320px]">
                    <div className="space-y-8">
                        <section className="space-y-5">
                            <div className="flex items-center gap-2">
                                <User size={18} className="text-gray-500" />
                                <h3 className="text-base font-semibold text-gray-900">Basic info</h3>
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Name</label>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={(e) => updateField("name", e.target.value)}
                                        className={inputClass}
                                        placeholder="John Smith"
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Nickname</label>
                                    <input
                                        type="text"
                                        value={form.nickname}
                                        onChange={(e) => updateField("nickname", e.target.value)}
                                        className={inputClass}
                                        placeholder="nickname"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Country</label>
                                <input
                                    type="text"
                                    value={form.country}
                                    onChange={(e) => updateField("country", e.target.value)}
                                    className={inputClass}
                                    placeholder="Canada"
                                />
                            </div>
                        </section>

                        <section className="space-y-5 border-t border-gray-100 pt-8">
                            <div className="flex items-center gap-2">
                                <Globe size={18} className="text-gray-500" />
                                <h3 className="text-base font-semibold text-gray-900">Social links</h3>
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Twitter</label>
                                    <input
                                        type="url"
                                        value={form.twitter}
                                        onChange={(e) => updateField("twitter", e.target.value)}
                                        className={inputClass}
                                        placeholder="https://x.com/username"
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Twitch</label>
                                    <input
                                        type="url"
                                        value={form.twitch}
                                        onChange={(e) => updateField("twitch", e.target.value)}
                                        className={inputClass}
                                        placeholder="https://twitch.tv/username"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>HLTV</label>
                                <input
                                    type="url"
                                    value={form.hltv}
                                    onChange={(e) => updateField("hltv", e.target.value)}
                                    className={inputClass}
                                    placeholder="https://www.hltv.org/player/..."
                                />
                            </div>
                        </section>

                        <section className="space-y-5 border-t border-gray-100 pt-8">
                            <div className="flex items-center gap-2">
                                <Twitch size={18} className="text-gray-500" />
                                <h3 className="text-base font-semibold text-gray-900">Media</h3>
                            </div>

                            <div>
                                <label className={labelClass}>Image URL</label>
                                <input
                                    type="url"
                                    value={form.image}
                                    onChange={(e) => updateField("image", e.target.value)}
                                    className={inputClass}
                                    placeholder="https://example.com/player.jpg"
                                />
                            </div>
                        </section>
                    </div>

                    <aside className="space-y-4">
                        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                            <div className="mb-3 flex items-center gap-2">
                                <ImageIcon size={18} className="text-gray-500" />
                                <h3 className="text-sm font-semibold text-gray-900">Preview</h3>
                            </div>

                            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                                {form.image ? (
                                    <img
                                        src={form.image}
                                        alt={form.nickname || form.name || "Player preview"}
                                        className="h-72 w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-72 items-center justify-center text-sm text-gray-400">
                                        No image selected
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 space-y-1">
                                <div className="text-lg font-semibold text-gray-900">
                                    {form.nickname || "Nickname"}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {form.name || "Player name"}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {form.country || "Country"}
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>

                <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-5 sm:flex-row sm:justify-end sm:px-8">
                    <Link
                        href="/admin/teams"
                        className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                    >
                        Cancel
                    </Link>

                    <button
                        type="submit"
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                    >
                        <Save size={16} />
                        {isNew ? "Create Player" : "Save Changes"}
                    </button>
                </div>
            </div>
        </form>
    );
}